import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Save, Plus, Trash2, Image, Type, Search, Globe, HelpCircle, FileText, Phone, Building2, ExternalLink } from "lucide-react";

export default function AdminCMS() {
  const { api, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [seo, setSeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) { navigate("/login"); return; }
    loadAll();
  }, [isAdmin, authLoading]);

  const loadAll = async () => {
    try {
      const [cRes, sRes] = await Promise.all([
        api("get", "/site-content"),
        api("get", "/seo-settings")
      ]);
      setContent(cRes.data);
      setSeo(sRes.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      await api("put", "/admin/site-content", {
        hero: content.hero,
        about: content.about,
        features: content.features,
        cta: content.cta,
        footer: content.footer,
        about_page: content.about_page,
        contact_page: content.contact_page,
        tenants: content.tenants,
      });
      toast.success("Homepage content saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveSeo = async () => {
    setSaving(true);
    try {
      await api("put", "/admin/seo-settings", {
        meta_title: seo.meta_title,
        meta_description: seo.meta_description,
        og_title: seo.og_title,
        og_description: seo.og_description,
        og_image: seo.og_image,
        structured_data: seo.structured_data,
        geo_content: seo.geo_content,
      });
      toast.success("SEO & GEO settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const updateHero = (key, val) => setContent(p => ({ ...p, hero: { ...p.hero, [key]: val } }));
  const updateAbout = (key, val) => setContent(p => ({ ...p, about: { ...p.about, [key]: val } }));
  const updateCta = (key, val) => setContent(p => ({ ...p, cta: { ...p.cta, [key]: val } }));
  const updateFooter = (key, val) => setContent(p => ({ ...p, footer: { ...p.footer, [key]: val } }));
  const updateFeature = (idx, key, val) => {
    const f = [...content.features];
    f[idx] = { ...f[idx], [key]: val };
    setContent(p => ({ ...p, features: f }));
  };
  const addFeature = () => setContent(p => ({ ...p, features: [...p.features, { title: "", description: "" }] }));
  const removeFeature = (idx) => setContent(p => ({ ...p, features: p.features.filter((_, i) => i !== idx) }));

  const updateStat = (idx, key, val) => {
    const s = [...content.about.stats];
    s[idx] = { ...s[idx], [key]: val };
    updateAbout("stats", s);
  };
  const addStat = () => updateAbout("stats", [...(content.about.stats || []), { value: "", label: "" }]);
  const removeStat = (idx) => updateAbout("stats", content.about.stats.filter((_, i) => i !== idx));

  const updateStructured = (key, val) => setSeo(p => ({ ...p, structured_data: { ...p.structured_data, [key]: val } }));
  const updateGeo = (key, val) => setSeo(p => ({ ...p, geo_content: { ...p.geo_content, [key]: val } }));

  const updateFaq = (idx, key, val) => {
    const f = [...(seo.geo_content?.faq || [])];
    f[idx] = { ...f[idx], [key]: val };
    updateGeo("faq", f);
  };
  const addFaq = () => updateGeo("faq", [...(seo.geo_content?.faq || []), { question: "", answer: "" }]);
  const removeFaq = (idx) => updateGeo("faq", (seo.geo_content?.faq || []).filter((_, i) => i !== idx));

  const updateKeyFact = (idx, val) => {
    const f = [...(seo.geo_content?.key_facts || [])];
    f[idx] = val;
    updateGeo("key_facts", f);
  };
  const addKeyFact = () => updateGeo("key_facts", [...(seo.geo_content?.key_facts || []), ""]);
  const removeKeyFact = (idx) => updateGeo("key_facts", (seo.geo_content?.key_facts || []).filter((_, i) => i !== idx));

  // About Page helpers
  const updateAboutPage = (key, val) => setContent(p => ({ ...p, about_page: { ...(p.about_page || {}), [key]: val } }));
  const updateAboutValue = (idx, key, val) => {
    const v = [...(content.about_page?.values || [])];
    v[idx] = { ...v[idx], [key]: val };
    updateAboutPage("values", v);
  };
  const addAboutValue = () => updateAboutPage("values", [...(content.about_page?.values || []), { title: "", description: "" }]);
  const removeAboutValue = (idx) => updateAboutPage("values", (content.about_page?.values || []).filter((_, i) => i !== idx));

  // Contact Page helpers
  const updateContactPage = (key, val) => setContent(p => ({ ...p, contact_page: { ...(p.contact_page || {}), [key]: val } }));

  // Tenants helpers
  const updateTenant = (idx, key, val) => {
    const t = [...(content.tenants || [])];
    t[idx] = { ...t[idx], [key]: val };
    setContent(p => ({ ...p, tenants: t }));
  };
  const addTenant = () => setContent(p => ({ ...p, tenants: [...(p.tenants || []), { name: "", logo_url: "", website_url: "" }] }));
  const removeTenant = (idx) => setContent(p => ({ ...p, tenants: (p.tenants || []).filter((_, i) => i !== idx) }));

  if (loading || !content || !seo) return <div className="max-w-5xl mx-auto px-6 py-12"><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 animate-fade-in" data-testid="admin-cms">
      <div className="mb-8">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl">Site Management</h1>
        <p className="text-muted-foreground mt-1">Edit homepage content, SEO, and GEO settings</p>
      </div>

      <Tabs defaultValue="homepage" className="space-y-6">
        <TabsList className="bg-muted rounded-none flex-wrap h-auto gap-0">
          <TabsTrigger value="homepage" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-homepage">
            <Type className="h-4 w-4 mr-2" />Homepage
          </TabsTrigger>
          <TabsTrigger value="about" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-about">
            <FileText className="h-4 w-4 mr-2" />About Page
          </TabsTrigger>
          <TabsTrigger value="contact" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-contact">
            <Phone className="h-4 w-4 mr-2" />Contact Page
          </TabsTrigger>
          <TabsTrigger value="tenants" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-tenants">
            <Building2 className="h-4 w-4 mr-2" />Tenants
          </TabsTrigger>
          <TabsTrigger value="seo" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-seo">
            <Search className="h-4 w-4 mr-2" />SEO
          </TabsTrigger>
          <TabsTrigger value="geo" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-geo">
            <Globe className="h-4 w-4 mr-2" />GEO
          </TabsTrigger>
        </TabsList>

        {/* HOMEPAGE TAB */}
        <TabsContent value="homepage" className="space-y-6">
          {/* Hero Section */}
          <Card className="p-6 border border-border rounded-none" data-testid="cms-hero-section">
            <h2 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2"><Image className="h-4 w-4" />Hero Section</h2>
            <div className="grid gap-4">
              <div><Label>Title (use \n for line breaks)</Label><Input value={content.hero?.title || ""} onChange={e => updateHero("title", e.target.value)} data-testid="cms-hero-title" /></div>
              <div><Label>Subtitle</Label><Input value={content.hero?.subtitle || ""} onChange={e => updateHero("subtitle", e.target.value)} data-testid="cms-hero-subtitle" /></div>
              <div><Label>Description</Label><Textarea value={content.hero?.description || ""} onChange={e => updateHero("description", e.target.value)} rows={3} data-testid="cms-hero-desc" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Primary CTA Text</Label><Input value={content.hero?.cta_text || ""} onChange={e => updateHero("cta_text", e.target.value)} data-testid="cms-hero-cta" /></div>
                <div><Label>Primary CTA Link</Label><Input value={content.hero?.cta_link || ""} onChange={e => updateHero("cta_link", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Secondary CTA Text</Label><Input value={content.hero?.secondary_cta_text || ""} onChange={e => updateHero("secondary_cta_text", e.target.value)} /></div>
                <div><Label>Secondary CTA Link</Label><Input value={content.hero?.secondary_cta_link || ""} onChange={e => updateHero("secondary_cta_link", e.target.value)} /></div>
              </div>
              <div><Label>Background Image URL</Label><Input value={content.hero?.background_image || ""} onChange={e => updateHero("background_image", e.target.value)} data-testid="cms-hero-bg" /></div>
              {content.hero?.background_image && <img src={content.hero.background_image} alt="Hero preview" className="h-32 w-full object-cover rounded border" />}
            </div>
          </Card>

          {/* About Section */}
          <Card className="p-6 border border-border rounded-none" data-testid="cms-about-section">
            <h2 className="font-heading font-semibold text-lg mb-4">About Section</h2>
            <div className="grid gap-4">
              <div><Label>Section Label</Label><Input value={content.about?.label || ""} onChange={e => updateAbout("label", e.target.value)} /></div>
              <div><Label>Title</Label><Input value={content.about?.title || ""} onChange={e => updateAbout("title", e.target.value)} data-testid="cms-about-title" /></div>
              <div><Label>Description</Label><Textarea value={content.about?.description || ""} onChange={e => updateAbout("description", e.target.value)} rows={3} data-testid="cms-about-desc" /></div>
              <div><Label>Image URL</Label><Input value={content.about?.image || ""} onChange={e => updateAbout("image", e.target.value)} data-testid="cms-about-img" /></div>
              {content.about?.image && <img src={content.about.image} alt="About preview" className="h-32 w-full object-cover rounded border" />}
              <Separator />
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium">Stats</Label>
                  <Button size="sm" variant="outline" onClick={addStat} data-testid="cms-add-stat"><Plus className="h-3 w-3 mr-1" />Add</Button>
                </div>
                {content.about?.stats?.map((s, i) => (
                  <div key={i} className="flex gap-2 mb-2 items-center">
                    <Input value={s.value} onChange={e => updateStat(i, "value", e.target.value)} placeholder="Value" className="w-24" />
                    <Input value={s.label} onChange={e => updateStat(i, "label", e.target.value)} placeholder="Label" className="flex-1" />
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeStat(i)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Features Section */}
          <Card className="p-6 border border-border rounded-none" data-testid="cms-features-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg">Features</h2>
              <Button size="sm" variant="outline" onClick={addFeature} data-testid="cms-add-feature"><Plus className="h-3 w-3 mr-1" />Add Feature</Button>
            </div>
            {content.features?.map((f, i) => (
              <div key={i} className="flex gap-3 mb-3 items-start">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input value={f.title} onChange={e => updateFeature(i, "title", e.target.value)} placeholder="Title" data-testid={`cms-feature-title-${i}`} />
                  <Input value={f.description} onChange={e => updateFeature(i, "description", e.target.value)} placeholder="Description" data-testid={`cms-feature-desc-${i}`} />
                </div>
                <Button size="sm" variant="ghost" className="text-destructive mt-1" onClick={() => removeFeature(i)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            ))}
          </Card>

          {/* CTA Section */}
          <Card className="p-6 border border-border rounded-none" data-testid="cms-cta-section">
            <h2 className="font-heading font-semibold text-lg mb-4">Call to Action Section</h2>
            <div className="grid gap-4">
              <div><Label>Title</Label><Input value={content.cta?.title || ""} onChange={e => updateCta("title", e.target.value)} data-testid="cms-cta-title" /></div>
              <div><Label>Description</Label><Textarea value={content.cta?.description || ""} onChange={e => updateCta("description", e.target.value)} rows={2} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Button Text</Label><Input value={content.cta?.button_text || ""} onChange={e => updateCta("button_text", e.target.value)} /></div>
                <div><Label>Button Link</Label><Input value={content.cta?.button_link || ""} onChange={e => updateCta("button_link", e.target.value)} /></div>
              </div>
              <div><Label>Background Image URL</Label><Input value={content.cta?.background_image || ""} onChange={e => updateCta("background_image", e.target.value)} data-testid="cms-cta-bg" /></div>
              {content.cta?.background_image && <img src={content.cta.background_image} alt="CTA preview" className="h-32 w-full object-cover rounded border" />}
            </div>
          </Card>

          {/* Footer Section */}
          <Card className="p-6 border border-border rounded-none" data-testid="cms-footer-section">
            <h2 className="font-heading font-semibold text-lg mb-4">Footer</h2>
            <div className="grid gap-4">
              <div><Label>Description</Label><Textarea value={content.footer?.description || ""} onChange={e => updateFooter("description", e.target.value)} rows={2} /></div>
              <div><Label>Contact Email</Label><Input value={content.footer?.email || ""} onChange={e => updateFooter("email", e.target.value)} /></div>
              <div><Label>Copyright Text</Label><Input value={content.footer?.copyright || ""} onChange={e => updateFooter("copyright", e.target.value)} /></div>
            </div>
          </Card>

          <Button onClick={saveContent} disabled={saving} className="w-full bg-foreground text-background rounded-none h-12 text-sm uppercase tracking-wider" data-testid="save-content-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? "Saving..." : "Save Homepage Content"}
          </Button>
        </TabsContent>

        {/* ABOUT PAGE TAB */}
        <TabsContent value="about" className="space-y-6">
          <Card className="p-6 border border-border rounded-none" data-testid="cms-aboutpage-hero">
            <h2 className="font-heading font-semibold text-lg mb-4">About Page Hero</h2>
            <div className="grid gap-4">
              <div><Label>Title</Label><Input value={content.about_page?.hero_title || ""} onChange={e => updateAboutPage("hero_title", e.target.value)} data-testid="cms-aboutpage-title" /></div>
              <div><Label>Subtitle</Label><Input value={content.about_page?.hero_subtitle || ""} onChange={e => updateAboutPage("hero_subtitle", e.target.value)} /></div>
              <div><Label>Hero Image URL</Label><Input value={content.about_page?.hero_image || ""} onChange={e => updateAboutPage("hero_image", e.target.value)} /></div>
              {content.about_page?.hero_image && <img src={content.about_page.hero_image} alt="Preview" className="h-32 w-full object-cover rounded border" />}
            </div>
          </Card>

          <Card className="p-6 border border-border rounded-none" data-testid="cms-aboutpage-story">
            <h2 className="font-heading font-semibold text-lg mb-4">Our Story</h2>
            <div className="grid gap-4">
              <div><Label>Section Title</Label><Input value={content.about_page?.story_title || ""} onChange={e => updateAboutPage("story_title", e.target.value)} /></div>
              <div><Label>Story Text</Label><Textarea value={content.about_page?.story_text || ""} onChange={e => updateAboutPage("story_text", e.target.value)} rows={6} data-testid="cms-aboutpage-story-text" /></div>
            </div>
          </Card>

          <Card className="p-6 border border-border rounded-none" data-testid="cms-aboutpage-mission">
            <h2 className="font-heading font-semibold text-lg mb-4">Mission</h2>
            <div className="grid gap-4">
              <div><Label>Mission Title</Label><Input value={content.about_page?.mission_title || ""} onChange={e => updateAboutPage("mission_title", e.target.value)} /></div>
              <div><Label>Mission Text</Label><Textarea value={content.about_page?.mission_text || ""} onChange={e => updateAboutPage("mission_text", e.target.value)} rows={3} /></div>
            </div>
          </Card>

          <Card className="p-6 border border-border rounded-none" data-testid="cms-aboutpage-values">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg">Values</h2>
              <Button size="sm" variant="outline" onClick={addAboutValue} data-testid="cms-add-value"><Plus className="h-3 w-3 mr-1" />Add Value</Button>
            </div>
            {content.about_page?.values?.map((v, i) => (
              <div key={i} className="flex gap-3 mb-3 items-start">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input value={v.title} onChange={e => updateAboutValue(i, "title", e.target.value)} placeholder="Value title" />
                  <Input value={v.description} onChange={e => updateAboutValue(i, "description", e.target.value)} placeholder="Description" />
                </div>
                <Button size="sm" variant="ghost" className="text-destructive mt-1" onClick={() => removeAboutValue(i)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            ))}
          </Card>

          <Button onClick={saveContent} disabled={saving} className="w-full bg-foreground text-background rounded-none h-12 text-sm uppercase tracking-wider" data-testid="save-about-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? "Saving..." : "Save About Page"}
          </Button>
        </TabsContent>

        {/* CONTACT PAGE TAB */}
        <TabsContent value="contact" className="space-y-6">
          <Card className="p-6 border border-border rounded-none" data-testid="cms-contactpage">
            <h2 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2"><Phone className="h-4 w-4" />Contact Page</h2>
            <div className="grid gap-4">
              <div><Label>Page Title</Label><Input value={content.contact_page?.title || ""} onChange={e => updateContactPage("title", e.target.value)} data-testid="cms-contact-title" /></div>
              <div><Label>Subtitle</Label><Input value={content.contact_page?.subtitle || ""} onChange={e => updateContactPage("subtitle", e.target.value)} /></div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email</Label><Input value={content.contact_page?.email || ""} onChange={e => updateContactPage("email", e.target.value)} data-testid="cms-contact-email" /></div>
                <div><Label>Phone</Label><Input value={content.contact_page?.phone || ""} onChange={e => updateContactPage("phone", e.target.value)} /></div>
              </div>
              <div><Label>Address</Label><Input value={content.contact_page?.address || ""} onChange={e => updateContactPage("address", e.target.value)} /></div>
              <div><Label>Business Hours</Label><Input value={content.contact_page?.hours || ""} onChange={e => updateContactPage("hours", e.target.value)} /></div>
              <div><Label>Google Maps Embed (iframe HTML)</Label><Textarea value={content.contact_page?.map_embed || ""} onChange={e => updateContactPage("map_embed", e.target.value)} rows={3} placeholder='<iframe src="https://maps.google.com/..." ...></iframe>' /></div>
            </div>
          </Card>

          <Button onClick={saveContent} disabled={saving} className="w-full bg-foreground text-background rounded-none h-12 text-sm uppercase tracking-wider" data-testid="save-contact-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? "Saving..." : "Save Contact Page"}
          </Button>
        </TabsContent>

        {/* TENANTS TAB */}
        <TabsContent value="tenants" className="space-y-6">
          <div className="bg-muted/50 border border-border rounded-none p-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1 flex items-center gap-2"><Building2 className="h-4 w-4" />Tenants & Members Logo Wall</p>
            <p>Add tenant/member logos that appear on the homepage. Each logo links to the company website.</p>
          </div>

          <Card className="p-6 border border-border rounded-none" data-testid="cms-tenants">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg">Tenants</h2>
              <Button size="sm" variant="outline" onClick={addTenant} data-testid="cms-add-tenant"><Plus className="h-3 w-3 mr-1" />Add Tenant</Button>
            </div>
            {(content.tenants || []).map((t, i) => (
              <div key={i} className="border border-border rounded p-4 mb-3" data-testid={`cms-tenant-${i}`}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div><Label className="text-xs">Name</Label><Input value={t.name} onChange={e => updateTenant(i, "name", e.target.value)} placeholder="Company name" /></div>
                  <div><Label className="text-xs">Logo URL</Label><Input value={t.logo_url} onChange={e => updateTenant(i, "logo_url", e.target.value)} placeholder="https://..." /></div>
                  <div><Label className="text-xs flex items-center gap-1">Website <ExternalLink className="h-3 w-3" /></Label><Input value={t.website_url} onChange={e => updateTenant(i, "website_url", e.target.value)} placeholder="https://..." /></div>
                </div>
                {t.logo_url && <img src={t.logo_url} alt={t.name} className="h-10 mt-2 object-contain" />}
                <Button size="sm" variant="ghost" className="text-destructive mt-2" onClick={() => removeTenant(i)}><Trash2 className="h-3 w-3 mr-1" />Remove</Button>
              </div>
            ))}
          </Card>

          <Button onClick={saveContent} disabled={saving} className="w-full bg-foreground text-background rounded-none h-12 text-sm uppercase tracking-wider" data-testid="save-tenants-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? "Saving..." : "Save Tenants"}
          </Button>
        </TabsContent>

        {/* SEO TAB */}
        <TabsContent value="seo" className="space-y-6">
          <Card className="p-6 border border-border rounded-none" data-testid="cms-seo-meta">
            <h2 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2"><Search className="h-4 w-4" />Meta Tags</h2>
            <div className="grid gap-4">
              <div><Label>Meta Title</Label><Input value={seo.meta_title || ""} onChange={e => setSeo(p => ({ ...p, meta_title: e.target.value }))} data-testid="seo-meta-title" /></div>
              <div><Label>Meta Description</Label><Textarea value={seo.meta_description || ""} onChange={e => setSeo(p => ({ ...p, meta_description: e.target.value }))} rows={3} data-testid="seo-meta-desc" /></div>
            </div>
          </Card>

          <Card className="p-6 border border-border rounded-none" data-testid="cms-seo-og">
            <h2 className="font-heading font-semibold text-lg mb-4">Open Graph</h2>
            <div className="grid gap-4">
              <div><Label>OG Title</Label><Input value={seo.og_title || ""} onChange={e => setSeo(p => ({ ...p, og_title: e.target.value }))} data-testid="seo-og-title" /></div>
              <div><Label>OG Description</Label><Textarea value={seo.og_description || ""} onChange={e => setSeo(p => ({ ...p, og_description: e.target.value }))} rows={2} /></div>
              <div><Label>OG Image URL</Label><Input value={seo.og_image || ""} onChange={e => setSeo(p => ({ ...p, og_image: e.target.value }))} /></div>
            </div>
          </Card>

          <Card className="p-6 border border-border rounded-none" data-testid="cms-seo-structured">
            <h2 className="font-heading font-semibold text-lg mb-4">Structured Data (Schema.org)</h2>
            <div className="grid gap-4">
              <div><Label>Business Name</Label><Input value={seo.structured_data?.name || ""} onChange={e => updateStructured("name", e.target.value)} /></div>
              <div><Label>Business Type</Label><Input value={seo.structured_data?.type || ""} onChange={e => updateStructured("type", e.target.value)} /></div>
              <div><Label>Description</Label><Textarea value={seo.structured_data?.description || ""} onChange={e => updateStructured("description", e.target.value)} rows={2} /></div>
              <div><Label>Address</Label><Input value={seo.structured_data?.address || ""} onChange={e => updateStructured("address", e.target.value)} data-testid="seo-address" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Phone</Label><Input value={seo.structured_data?.phone || ""} onChange={e => updateStructured("phone", e.target.value)} /></div>
                <div><Label>Email</Label><Input value={seo.structured_data?.email || ""} onChange={e => updateStructured("email", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Website URL</Label><Input value={seo.structured_data?.url || ""} onChange={e => updateStructured("url", e.target.value)} /></div>
                <div><Label>Price Range</Label><Input value={seo.structured_data?.price_range || ""} onChange={e => updateStructured("price_range", e.target.value)} /></div>
              </div>
              <div><Label>Opening Hours</Label><Input value={seo.structured_data?.opening_hours || ""} onChange={e => updateStructured("opening_hours", e.target.value)} placeholder="Mo-Su 08:00-22:00" /></div>
            </div>
          </Card>

          <Button onClick={saveSeo} disabled={saving} className="w-full bg-foreground text-background rounded-none h-12 text-sm uppercase tracking-wider" data-testid="save-seo-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? "Saving..." : "Save SEO Settings"}
          </Button>
        </TabsContent>

        {/* GEO TAB */}
        <TabsContent value="geo" className="space-y-6">
          <div className="bg-muted/50 border border-border rounded-none p-4 text-sm text-muted-foreground" data-testid="geo-info">
            <p className="font-medium text-foreground mb-1 flex items-center gap-2"><Globe className="h-4 w-4" />Generative Engine Optimization (GEO)</p>
            <p>Optimize your content for AI-powered search engines like Google SGE, Perplexity, and Bing Chat. Structured, factual content improves visibility in AI-generated responses.</p>
          </div>

          <Card className="p-6 border border-border rounded-none" data-testid="cms-geo-entity">
            <h2 className="font-heading font-semibold text-lg mb-4">Entity Description</h2>
            <Textarea
              value={seo.geo_content?.entity_description || ""}
              onChange={e => updateGeo("entity_description", e.target.value)}
              rows={4}
              placeholder="Comprehensive description of your business for AI engines..."
              data-testid="geo-entity-desc"
            />
          </Card>

          <Card className="p-6 border border-border rounded-none" data-testid="cms-geo-facts">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg">Key Facts</h2>
              <Button size="sm" variant="outline" onClick={addKeyFact} data-testid="geo-add-fact"><Plus className="h-3 w-3 mr-1" />Add</Button>
            </div>
            {seo.geo_content?.key_facts?.map((f, i) => (
              <div key={i} className="flex gap-2 mb-2 items-center">
                <Input value={f} onChange={e => updateKeyFact(i, e.target.value)} placeholder="Key fact..." className="flex-1" />
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeKeyFact(i)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            ))}
          </Card>

          <Card className="p-6 border border-border rounded-none" data-testid="cms-geo-faq">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2"><HelpCircle className="h-4 w-4" />FAQ (Schema.org FAQPage)</h2>
              <Button size="sm" variant="outline" onClick={addFaq} data-testid="geo-add-faq"><Plus className="h-3 w-3 mr-1" />Add FAQ</Button>
            </div>
            {seo.geo_content?.faq?.map((f, i) => (
              <div key={i} className="border border-border rounded p-3 mb-3">
                <div className="flex items-start gap-2">
                  <div className="flex-1 grid gap-2">
                    <Input value={f.question} onChange={e => updateFaq(i, "question", e.target.value)} placeholder="Question" data-testid={`faq-question-${i}`} />
                    <Textarea value={f.answer} onChange={e => updateFaq(i, "answer", e.target.value)} placeholder="Answer" rows={2} data-testid={`faq-answer-${i}`} />
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeFaq(i)}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            ))}
          </Card>

          <Button onClick={saveSeo} disabled={saving} className="w-full bg-foreground text-background rounded-none h-12 text-sm uppercase tracking-wider" data-testid="save-geo-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? "Saving..." : "Save GEO Settings"}
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
